import React from 'react';

import InsightsIcon from '@mui/icons-material/InsightsOutlined';
import InventoryIcon from '@mui/icons-material/InventoryOutlined';
import MenuBookIcon from '@mui/icons-material/MenuBookOutlined';
import PeopleIcon from '@mui/icons-material/PeopleOutlined';
import PriceChangeOutlinedIcon from '@mui/icons-material/PriceChangeOutlined';

import defaultDashboardIcon from '../../../assets/images/ico_custom_dashboard.svg';
import orderReportIcon from '../../../assets/images/ico_reports_orders.svg';
import reportOverviewIcon from '../../../assets/images/ico_reports_overview.svg';
import reportsEndOfDayIcon from '../../../assets/images/reports-end-of-day.svg';
import { type BadgeLabel } from '../../../ui/atoms/Badge/types';
import { MenuSource } from '../../RMS/rms.services';
import { RMSSubscriptions } from '../../RMS/utils/rmsSubscriptions';
import { REPORT_ICONS_URL_PREFIX } from '../constants';
import { CategoryVisibility, ClientCategory } from '../types/clientCategory';
import { LookerDashboard } from '../types/dashboard';
import { getDashboardVisibility } from './dynamicDashboardVisibility';
import { isReportVisible } from './isReportVisible';
import { getDashboardLabels, REPORT_LABELS } from './reportLabels';
import { VisibilityPresets } from './visibilityPresets';

type ReportMenuItem = {
  title: TranslationId;
  path: string;
  icon: JSX.Element;
  searchAlias?: string[];
  subtitle?: TranslationId;
  subscription?: RMSSubscriptions;
  isCustom?: boolean;
  hidden?: boolean;
  dynamicDashboard?: string;
  visibility?: CategoryVisibility;
  labels?: BadgeLabel[];
};

type DynamicMenuItemInitial = {
  title: TranslationId;
  searchAlias: string[];
};

// TODO: Remove 'legacy' mode when reportCategoryVisibility flag is removed
export type VisibilityContext =
  | { mode: 'legacy'; menuSource?: MenuSource; subscriptions: RMSSubscriptions[] }
  | { mode: 'category'; category: ClientCategory; subscriptions: RMSSubscriptions[] };

const ICON_STYLE = { opacity: 0.54, width: 24, height: 24, color: 'rgba(0, 0, 0, 0.54)' };

const getReportIcon = (dashboard?: LookerDashboard): JSX.Element => {
  const src = dashboard?.IconFileName
    ? REPORT_ICONS_URL_PREFIX + dashboard.IconFileName
    : defaultDashboardIcon;
  return <img style={ICON_STYLE} src={src} />;
};

const MENU_ITEM_TEMPLATES: Record<string, Omit<ReportMenuItem, 'path'>> = {
  OVERVIEW: {
    title: 'Overview' as TranslationId,
    icon: <img style={ICON_STYLE} src={reportOverviewIcon} />,
    subtitle: 'Overview_report_description' as TranslationId,
    searchAlias: [
      'Online order reports',
      'App order report',
      'online order report',
      'online order refunds',
      'orders report',
      'rejected orders',
      'accepted orders',
    ],
    visibility: VisibilityPresets.onlineOnly,
    labels: [REPORT_LABELS.ONLINE],
  },
  ORDERS: {
    title: 'Orders' as TranslationId,
    icon: <img style={ICON_STYLE} src={orderReportIcon} />,
    subtitle: 'Orders_report_description' as TranslationId,
    searchAlias: [
      'orders per sales channel',
      'order per type',
      'orders per payment method',
      'orders per location',
      'rejected orders',
      'reason of rejection',
      'rejects per location',
      'rejections per store',
    ],
    visibility: VisibilityPresets.onlineOnly,
    labels: [REPORT_LABELS.ONLINE],
  },
  END_OF_DAY: {
    title: 'End_of_day_report' as TranslationId,
    icon: <img src={reportsEndOfDayIcon} />,
    subtitle: 'End_of_day_report_description' as TranslationId,
    searchAlias: [
      'End of day sumup',
      'EOD report',
      'close of business report',
      'COB report',
      'end of business report',
      'EOB',
    ],
    visibility: VisibilityPresets.onlineOnly,
    labels: [REPORT_LABELS.ONLINE],
  },
  RMS_SALES: {
    title: 'Sales' as TranslationId,
    icon: <InsightsIcon />,
    subtitle: 'Sales_BI_Tools' as TranslationId,
    searchAlias: [
      'POS reports',
      'rms reports',
      'Sales Overview',
      'Sales VAT',
      'End of Day Report',
      'Daily Float Sessions',
      'Kitchen Timings Report',
      'Sales Discounts',
      'Sales Refunds',
      'Sales Charges',
      'Hourly Sales',
    ],
    subscription: RMSSubscriptions.BASE,
    visibility: VisibilityPresets.posOnly,
  },
  RMS_COSTS: {
    title: 'Costs' as TranslationId,
    icon: <PriceChangeOutlinedIcon />,
    subtitle: 'Costs_BI_Tools' as TranslationId,
    searchAlias: [
      'POS reports',
      'rms reports',
      'costs overview report',
      'wages costs',
      'venue commissions',
      'daily COGS summary',
      'fixed costs',
      'staff meals',
      'guest meals',
      'freebies meals',
    ],
    subscription: RMSSubscriptions.BASE,
    visibility: VisibilityPresets.posOnly,
  },
  RMS_PAYROLL: {
    title: 'Payroll' as TranslationId,
    icon: <PeopleIcon />,
    subtitle: 'Payroll_BI_Tools' as TranslationId,
    searchAlias: [
      'POS reports',
      'rms reports',
      'Daily payroll costs',
      'daily sales by driver',
      'upselling points',
    ],
    subscription: RMSSubscriptions.PAYROLL,
    visibility: VisibilityPresets.posOnly,
  },
  RMS_INVENTORY: {
    title: 'Inventory' as TranslationId,
    icon: <InventoryIcon />,
    subtitle: 'Inventory_BI_Tools' as TranslationId,
    searchAlias: [
      'POS reports',
      'rms reports',
      'purchase orders',
      'wastages',
      'discrepancies',
      'inventory status',
      'daily COGS summary',
      'Sales by product',
    ],
    subscription: RMSSubscriptions.INVENTORY,
    visibility: VisibilityPresets.posOnly,
  },
  RMS_MENU: {
    title: 'Menu' as TranslationId,
    icon: <MenuBookIcon />,
    subtitle: 'Menu_BI_Tools' as TranslationId,
    searchAlias: [
      'POS reports',
      'rms reports',
      'Menu item sales',
      'hourly menu item sales',
      'reporting tag sales',
      'hourly reporting tag sales',
      'menu item refunds',
    ],
    subscription: RMSSubscriptions.BASE,
    visibility: VisibilityPresets.posOnly,
  },
};

const DYNAMIC_MENU_TEMPLATES: Record<string, DynamicMenuItemInitial> = {
  PERFORMANCE_SUMMARY: {
    title: 'Performance_summary' as TranslationId,
    searchAlias: ['Performance summary report', 'tax details report'],
  },
  CUSTOMER_REPORTS: {
    title: 'Customer_reports' as TranslationId,
    searchAlias: [
      'Customer lifetime orders',
      'customer weeks since last order',
      'new customer conversion',
      'repeat customer conversion',
      'unique customers',
      'new customers',
      'average order value',
      'average value per customer',
      'repeat customer rate by menu item',
      'repeat customer rate',
      'loyalty customer rate',
      'retention customer rate',
      'new repeat customers',
      'new loyalty customers',
      'new retention customers',
      'lapsing customers',
      'customer details',
    ],
  },
  BUSINESS_REVIEW: {
    title: 'Business_review_report_title' as TranslationId,
    searchAlias: [
      'Pickup per channel',
      'delivery by channel',
      'revenue report',
      'average order value report',
      'customer performance',
      'repeat customers report',
      'unique customers report',
      'ordered items count',
    ],
  },
  ORDERED_ITEMS: {
    title: 'Ordered_items_report' as TranslationId,
    searchAlias: ['Order count and revenue', 'ordered items breakdown', 'vouchers breakdown'],
  },
  SALES_REPORT: {
    title: 'Sales_report' as TranslationId,
    searchAlias: ['sales per store', 'sales per channel', 'sales per type'],
  },
  TIPS_REPORT: {
    title: 'Tips_report_title' as TranslationId,
    searchAlias: ['Tips Report'],
  },
  CAMPAIGN_PERFORMANCE: {
    title: 'Campaign_performance_report' as TranslationId,
    searchAlias: [
      'Marketing campaign report',
      'loyalty campaign report',
      'vouchers campaign report',
      'loyalty report',
      'retention report',
      'first order discount report',
      'voucher orders report',
    ],
  },
  STORE_OPERATIONS: {
    title: 'Store_operations_report' as TranslationId,
    searchAlias: [
      'Override duration by store',
      'order frequency',
      'delivery versus pickup',
      'sales channel operations report',
      'property operations report',
    ],
  },
};

const createMenuItem = (
  template: Omit<ReportMenuItem, 'path'>,
  appId: string,
  path: string,
  overrides: Partial<ReportMenuItem> = {}
): ReportMenuItem => ({
  ...template,
  path: `/${appId}/reports${path}`,
  ...overrides,
});

const removeRmsKeywordFromTitle = (title?: string) => {
  return title?.replace(/\(rms\)/gi, '').trim() || '';
};

export const getReportsMenuItems = (
  appId: string,
  dashboardsData: LookerDashboard[] = [],
  visibilityContext: VisibilityContext,
  showLabels?: boolean
): ReportMenuItem[] => {
  const menuItems: ReportMenuItem[] = [];

  // TODO: Keep only this branch when reportCategoryVisibility flag is removed
  if (visibilityContext.mode === 'category') {
    const { category, subscriptions } = visibilityContext;

    const coreReports = [
      { template: MENU_ITEM_TEMPLATES.OVERVIEW, path: '/overview' },
      { template: MENU_ITEM_TEMPLATES.ORDERS, path: '/orders' },
      { template: MENU_ITEM_TEMPLATES.END_OF_DAY, path: '/endofday' },
    ];

    coreReports.forEach(({ template, path }) => {
      if (
        template.visibility &&
        isReportVisible({
          visibility: template.visibility,
          category,
          requiredSubscription: template.subscription,
          clientSubscriptions: subscriptions,
        })
      ) {
        menuItems.push(createMenuItem(template, appId, path));
      }
    });

    dashboardsData.forEach((dashboard) => {
      const dashboardName = removeRmsKeywordFromTitle(dashboard.Name);
      const dashboardVisibility = getDashboardVisibility(dashboardName);

      if (
        isReportVisible({
          visibility: dashboardVisibility,
          category,
          clientSubscriptions: subscriptions,
        })
      ) {
        const aliases =
          Object.values(DYNAMIC_MENU_TEMPLATES).find(
            (item) => item.title?.toLowerCase() === dashboardName?.toLowerCase()
          )?.searchAlias || [];
        const template = {
          title: dashboardName as TranslationId,
          icon: getReportIcon(dashboard),
          subtitle: dashboard.Summary as TranslationId,
          searchAlias: aliases,
          visibility: dashboardVisibility,
          labels: getDashboardLabels(dashboardName),
        };
        const path = dashboard.IsWhiteLabelSpecific
          ? `/dashboards/${dashboard.DashboardLink}`
          : `/dashboards/global/${dashboard.DashboardLink}`;
        menuItems.push(createMenuItem(template, appId, path));
      }
    });

    const rmsReports = [
      { template: MENU_ITEM_TEMPLATES.RMS_SALES, path: '/rms/sales' },
      { template: MENU_ITEM_TEMPLATES.RMS_COSTS, path: '/rms/costs' },
      { template: MENU_ITEM_TEMPLATES.RMS_PAYROLL, path: '/rms/payroll' },
      { template: MENU_ITEM_TEMPLATES.RMS_INVENTORY, path: '/rms/inventory' },
      { template: MENU_ITEM_TEMPLATES.RMS_MENU, path: '/rms/menu' },
    ];

    rmsReports.forEach(({ template, path }) => {
      if (
        template.visibility &&
        isReportVisible({
          visibility: template.visibility,
          category,
          requiredSubscription: template.subscription,
          clientSubscriptions: subscriptions,
        })
      ) {
        menuItems.push(createMenuItem(template, appId, path));
      }
    });
  } else {
    // TODO: Remove this else branch when reportCategoryVisibility flag is removed
    const { menuSource, subscriptions } = visibilityContext;

    menuItems.push(createMenuItem(MENU_ITEM_TEMPLATES.OVERVIEW, appId, '/overview'));
    menuItems.push(createMenuItem(MENU_ITEM_TEMPLATES.ORDERS, appId, '/orders'));
    menuItems.push(createMenuItem(MENU_ITEM_TEMPLATES.END_OF_DAY, appId, '/endofday'));

    dashboardsData.forEach((dashboard) => {
      if (
        menuSource !== 'RMS' &&
        menuSource !== 'BOTH' &&
        dashboard.Name?.toLowerCase().includes('(rms)')
      ) {
        return;
      }
      const dashboardName = removeRmsKeywordFromTitle(dashboard.Name);
      const aliases =
        Object.values(DYNAMIC_MENU_TEMPLATES).find(
          (item) => item.title?.toLowerCase() === dashboardName?.toLowerCase()
        )?.searchAlias || [];
      const template = {
        title: dashboardName as TranslationId,
        icon: getReportIcon(dashboard),
        subtitle: dashboard.Summary as TranslationId,
        searchAlias: aliases,
        labels: getDashboardLabels(dashboardName),
      };
      const path = dashboard.IsWhiteLabelSpecific
        ? `/dashboards/${dashboard.DashboardLink}`
        : `/dashboards/global/${dashboard.DashboardLink}`;
      menuItems.push(createMenuItem(template, appId, path));
    });

    const rmsReports = [
      { template: MENU_ITEM_TEMPLATES.RMS_SALES, path: '/rms/sales' },
      { template: MENU_ITEM_TEMPLATES.RMS_COSTS, path: '/rms/costs' },
      { template: MENU_ITEM_TEMPLATES.RMS_PAYROLL, path: '/rms/payroll' },
      { template: MENU_ITEM_TEMPLATES.RMS_INVENTORY, path: '/rms/inventory' },
      { template: MENU_ITEM_TEMPLATES.RMS_MENU, path: '/rms/menu' },
    ];

    rmsReports.forEach(({ template, path }) => {
      if (!template.subscription || subscriptions.includes(template.subscription)) {
        menuItems.push(createMenuItem(template, appId, path));
      }
    });
  }

  if (showLabels === false) {
    return menuItems.map((item) => ({ ...item, labels: [] }));
  }

  return menuItems;
};
